import type { LbOptions, LbPostData, LbPostEvent } from '@uefacom-newdev/liveblog-integration-react';
import { isMatchEvent, LbEventSubType, LbEventType, useOptions } from '@uefacom-newdev/liveblog-integration-react';
import { PkIcon } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import parseHtmlToReact from 'html-react-parser';
import { memo, useMemo } from 'react';

import { getMatchEventIcon, getVarEventIcon } from '../utils';

import { trackCardElement } from '@services/GoogleTagManagerService';
import TeamFlag from '@shared/MatchUnit/utils/TeamFlag';
import { useTranslateFn } from '@shared/Translate';
import type { Draw } from '@shared-types/draw.types';
import { dateToString } from '@utilities/dateTime';

import * as classes from './styles.module.scss';

const getIcon = (event: LbPostEvent) => {
  if (!event.eventType) return null;

  let matchEventIcon = getMatchEventIcon(event.eventType, event.eventSubType);

  if (event.eventType === LbEventType.START_PHASE) {
    matchEventIcon = getMatchEventIcon(LbEventType.START_PHASE, event.eventPhase);
  }

  if (event.varInfo) {
    matchEventIcon = getVarEventIcon(event.varInfo);
  }

  return matchEventIcon;
};

const notTranslatedEvents: LbEventType[] = [
  LbEventType.SHOT_BLOCKED,
  LbEventType.SHOT_WIDE,
  LbEventType.OFFSIDE,
  LbEventType.CORNER,
  LbEventType.FOUL,
  LbEventType.ASSIST,
  LbEventType.START_PHASE,
  LbEventType.END_PHASE,
  LbEventType.PAUSE,
  LbEventType.RESUME,
  LbEventType.CHANGE_PHASE,
  LbEventType.FREE_KICK,
  LbEventType.SAVE,
  LbEventType.GOAL_OPPORTUNITY,
  LbEventType.FULL_TIME,
  LbEventType.CORNER_AWARDED,
  LbEventType.DANGEROUS_ATTACK,
  LbEventType.PENALTY_AWARDED,
  LbEventType.PENALTY_AWARDED,
];
export const EventLabel = ({ event }: { event: LbPostEvent }) => {
  const t = useTranslateFn();
  // events description are coming from API
  if (!event.eventType || notTranslatedEvents.includes(event.eventType))
    return parseHtmlToReact(event.eventTranslationFormatted ?? '');
  if (event.eventType === LbEventType.INJURY_TIME) {
    return event.eventStrTime === '0'
      ? //Event INJURY_TIME with no minutes added
        t('no_added_time')
      : //Event INJURY_TIME with minutes added
        `+${event.eventStrTime} ${event.eventTranslation}`;
  }
  //Event SUBSTITUTION with eventSubType HALF_TIME
  if (event.eventType === LbEventType.SUBSTITUTION && event.eventSubType === LbEventSubType.HALF_TIME)
    return t('lb-matchevent-4');
  //Event YELLOW_CARD
  if (event.eventType === LbEventType.YELLOW_CARD) return t('lb-matchevent-1');
  //Event RED_CARD
  if (event.eventType === LbEventType.RED_CARD) return t('lb-matchevent-2');

  if (event.eventType === LbEventType.VAR && event.varInfo?.status)
    return t(`lb-matchevent-VAR_${event.varInfo.status}`);

  //other event type
  const type = [event.eventType, event.eventSubType].filter(Boolean).join('_');
  return t(`lb-matchevent-${type}`);
};

export const Event = ({ event }: { event: LbPostEvent }) => {
  const { match, disableTeamLink } = useOptions();

  const icon = useMemo(() => {
    if (!event.eventType) return null;

    const matchEventIcon = getIcon(event);
    if (!matchEventIcon) return null;

    const { iconName, iconColor } = matchEventIcon;
    const isGoalEvent =
      event.eventType === LbEventType.GOAL ||
      (event.eventType === LbEventType.PENALTY && event.eventSubType === LbEventSubType.SCORED);

    return (
      <PkIcon
        pk-theme='light'
        className={clsx({
          [classes.pkIcon]: true,
          [classes.pkIconGoal]: isGoalEvent,
        })}
        name={iconName}
        color={iconColor}
      />
    );
  }, [event]);

  const flagEvents = [LbEventType.GOAL, LbEventType.SUBSTITUTION, LbEventType.PENALTY].includes(event.eventType);

  const ownGoal = event.eventType === LbEventType.GOAL && event.eventSubType === LbEventSubType.OWN;
  const teamsPlaying = useMemo(() => [match.homeTeam, match.awayTeam], [match.homeTeam, match.awayTeam]);
  const team = useMemo(
    () =>
      ownGoal
        ? teamsPlaying.filter(Boolean).find((t) => t.id !== event.idTeamTo)
        : teamsPlaying.filter(Boolean).find((t) => t.id === event.idTeam),
    [event.idTeam, event.idTeamTo, ownGoal, teamsPlaying],
  ); //If the goal is a Own goal we will show the flag of the team that got the point,

  const trackingData = {
    category: 'match commentary',
    variant: 'match event',
    name: event?.eventTranslation,
    id: event?.eventId,
  };

  return (
    icon && (
      <div className={classes.iconContainer} ref={trackCardElement(trackingData)}>
        <div className={classes.eventIcon}>
          {icon}
          <span className={classes.label}>
            <EventLabel event={event} />
          </span>
        </div>
        {flagEvents && <TeamFlag className={classes.teamFlag} team={team} teamLink={!disableTeamLink} size={24} />}
      </div>
    )
  );
};

const noMinuteEvents: LbEventType[] = [
  LbEventType.START_PHASE,
  LbEventType.END_PHASE,
  LbEventType.PAUSE,
  LbEventType.RESUME,
  LbEventType.CHANGE_PHASE,
  LbEventType.INJURY_TIME,
  LbEventType.VAR,
];
export const Header = ({ liveblogPostData, date }: { liveblogPostData: LbPostData; date?: string }) => {
  const { draw } = useOptions() as LbOptions & { draw: Draw };
  const event = liveblogPostData?.lbPostFSPEvent ?? ({} as LbPostEvent);
  const isInjuryTime = !!event?.eventDisplayInjuryTimeMinute;
  const minute = isInjuryTime ? `${event?.eventDisplayInjuryTimeMinute}'` : event?.eventDisplayMinute;
  const noMinuteEvent = noMinuteEvents.includes(event?.eventType) || !!event?.varInfo;
  const hasEvent = isMatchEvent(liveblogPostData) && getIcon(event);

  if (!draw && !hasEvent && (noMinuteEvent || !minute)) return null;

  return (
    <div
      className={clsx({
        [classes.header]: true,
        [classes.headerNoEvent]: !hasEvent,
        [classes.headerWithInjury]: isInjuryTime,
        [classes.isDraw]: draw,
      })}
    >
      {draw && <span className={classes.time}>{dateToString(date, 'HH:mm')}</span>}
      {!noMinuteEvent && !!minute && (
        <span className={classes.eventMinute}>
          {minute}
          {isInjuryTime && (
            <span className={classes.eventMinuteInjuryTime}>{`+ ${event?.eventDisplayInjuryTimeExtraMinute}`}</span>
          )}
        </span>
      )}
      {hasEvent && <Event event={event} />}
    </div>
  );
};

export default memo(Header);
